'use client';

import { handleRegistrationRedirect } from '@commerce-profile-webcomponents/ui-composite';
import { onSuccessPropType, RegFlowConfig, RoutePaths } from '@commerce-profile-webcomponents/sdk';
import { Registration, LoginSessionDetectedModal } from '@commerce-profile-webcomponents/ui';
import { ErrorCard, Errors, useSharecode } from '@commerce-webcomponents/ui';
import {
  AccountType,
  Button,
  ButtonAlignment,
  ButtonStyles,
  ButtonTypes,
  ButtonWidth,
  Modal,
  TextAlign,
  Theme,
  useNextgenRouter,
  useTranslation,
} from '@nextgen-web-framework/all';
import { ConfigService, Logger } from '@nextgen-web-framework/core';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../feature/store';
import { BasePageProps, LocalStorageKeys } from '../../../../types/common';
import styles from './SignupPage.module.scss';
import AdaChatbot from 'app/components/AdaChatbot/AdaChatbot';
import logObject from 'utils/logObject';
import ProtectedRoute from 'app/components/ProtectedRoute/ProtectedRoute';
import useLivePreview from 'live-preview/useLivePreview';
import { useRolesConfig } from 'utils/rolesConfig';

const logger = Logger.getInstance(logObject('index.tsx', 'Signup'));

const SignupPage = ({ data }: BasePageProps) => {
  const router = useNextgenRouter();
  const { UserRole } = useRolesConfig();
  const [hasError, setHasError] = useState(false);
  const flowName = router.query?.get('flowName') as RegFlowConfig;
  const stepName = router.query?.get('stepName') as string;
  const registrationId = router.query?.get('registrationId') as string;
  const sharecodeid = router.query?.get('sharecodeid');
  const returnUri = router.query?.get('returnUri') as string;
  const { cart } = useAppSelector((state) => state.commerceCart);
  const { hasError: hasCheckoutError } = useAppSelector((state) => state.commerceCheckout);
  const dispatch = useAppDispatch();
  const { sharecodeData } = useSharecode();
  const [leavingPage, setLeavingPage] = useState(false);
  const t = useTranslation();
  const configs = ConfigService.getConfigs();
  const isBackWarnEnabled = configs.NEXT_BROWSER_BACK_WARN_ENABLED;
  const renderRegistration = !sharecodeid || (sharecodeid && sharecodeData?.acctType);
  const [pageData] = useLivePreview(data, data?.seo_config);

  const [showSessionModal, setShowSessionModal] = useState(false);

  const handleBackWarn = () => {
    setLeavingPage(true);
  };

  useEffect(() => {
    if (isBackWarnEnabled) {
      router.events?.setIsBackBlocked(true);
      router.events?.on('browserBackWarn', handleBackWarn);
      window.history.pushState(null, document.title, window.location.href);
    }

    return () => {
      if (isBackWarnEnabled) {
        router.events?.setIsBackBlocked(false);
        router.events?.off('browserBackWarn', handleBackWarn);
        window.history.replaceState(null, document.title, window.location.href);
      }
    };
  }, []);

  const handleUnauthorizedAction = () => {
    if (flowName === RegFlowConfig.CoApplicantFlow) {
      setShowSessionModal(true);
      return;
    }
    router.push(RoutePaths.HOME);
  };

  let ROUTE = {
    findOutMore: '/signup/find-out-more',
    completeYourProfile: '',
    sourceUrl: '',
  };
  if (typeof window !== 'undefined') {
    ROUTE = {
      ...ROUTE,
      completeYourProfile: `${window?.location.origin}/complete-your-profile`,
      sourceUrl: window?.location.href,
    };
  }
  const onSuccess = ({
    registrationId,
    accountType,
    returnUrl: nextUrl,
    kitSku,
    feeSku,
    useNewCartForCheckout,
  }: onSuccessPropType) => {
    // `nextUrl` is to rewrite the final redirect url based on the registration flow
    // By default, it will be original value of `returnUri`
    // For skip cart flow, it will rewrite to /checkout
    handleRedirect(registrationId, accountType, nextUrl, kitSku, feeSku, useNewCartForCheckout);
  };
  const handleRedirect = async (
    registrationId: string,
    accountType: AccountType,
    nextUrl: string,
    kitSku?: string,
    feeSku?: string,
    useNewCartForCheckout?: boolean,
  ) => {
    try {
      const redirectTo = await handleRegistrationRedirect({
        dispatch,
        setHasError,
        registrationId,
        accountType,
        nextUrl,
        kitSku,
        feeSku,
        useNewCartForCheckout,
        cart,
        cartIdKey: LocalStorageKeys.CART_ID,
      });
      router.push(redirectTo);
    } catch (err) {
      logger.error(err, 'Error Occured');
      setHasError(true);
    }
  };
  if (hasError || hasCheckoutError) {
    return <ErrorCard type={Errors.General} onActionClick={() => router.refresh()} />;
  }
  logger.log(
    flowName,
    'flowname',
    'userRoleCode',
    flowName === RegFlowConfig.AboLedFlow ? [UserRole.ABO, UserRole.ANONYMOUS] : [UserRole.ANONYMOUS],
  );

  return (
    <>
      {flowName && (
        <ProtectedRoute
          allowedRoles={
            flowName === RegFlowConfig.AboLedFlow ? [UserRole.ABO, UserRole.ANONYMOUS] : [UserRole.ANONYMOUS]
          }
          handleUnauthorizedAction={handleUnauthorizedAction}
        >
          <div data-testid="signup-page-container" className={styles['signup-page-container']}>
            <>
              <div className={styles['sign-up-page']}>
                {renderRegistration && (
                  <Registration
                    flowName={flowName}
                    redirectUrl={returnUri ?? '/'}
                    sponsorNumber={sharecodeData?.accountId}
                    sponsorName={sharecodeData?.accountName}
                    accountType={sharecodeData?.registrationAccountType as AccountType}
                    findOutMoreUrl={ROUTE.findOutMore}
                    sourceUrl={ROUTE.sourceUrl}
                    completeYourProfileUrl={ROUTE.completeYourProfile}
                    onSuccess={onSuccess}
                    registrationId={registrationId ?? ''}
                    stepName={stepName ?? ''}
                    accountTypeImagesCms={pageData?.account_type_details?.[0]?.account_type_details}
                    profileInfoCms={pageData?.profile_info?.[0]}
                    termsAndConditionsCms={pageData?.terms_and_conditions?.[0]?.user_info}
                    registrationOrderConfirmImagesCMS={pageData?.registration_order_confirm?.thank_you_images?.images}
                  />
                )}
              </div>
            </>
            {isBackWarnEnabled && leavingPage ? (
              <Modal
                headline={t('amshop.signUp.headline')}
                primaryButtonLabel={''}
                closeIcon={false}
                useReactPortal={true}
                portalContainerId="react-portal-modal"
                textAlign={TextAlign.CENTER}
                buttonAlignment={ButtonAlignment.VERTICAL}
                primaryButton={
                  <Button
                    title={t('amshop.signUp.primaryButton.label')}
                    type={ButtonTypes.BUTTON}
                    onClick={() => {
                      router.events?.setIsBackBlocked(false);
                      window.location.href = '/';
                    }}
                    buttonStyle={ButtonStyles.SOLID}
                    theme={Theme.BLACK}
                    data-testid="primary-button-test-id"
                    buttonWidth={ButtonWidth.FULL_WIDTH}
                  />
                }
                secondaryButton={
                  <Button
                    title={t('amshop.signUp.secondaryButton.label')}
                    type={ButtonTypes.BUTTON}
                    onClick={() => {
                      setLeavingPage(false);
                    }}
                    buttonStyle={ButtonStyles.OUTLINE}
                    theme={Theme.BLACK}
                    data-testid="secondary-button-test-id"
                    buttonWidth={ButtonWidth.FULL_WIDTH}
                  />
                }
              >
                <>
                  <div className={styles['signUp-subheading']}>{t('amshop.signUp.subheader')}</div>
                </>
              </Modal>
            ) : null}
          </div>
        </ProtectedRoute>
      )}
      {configs.NEXT_ADA_DATA_HANDLE && <AdaChatbot />}
      {showSessionModal && <LoginSessionDetectedModal />}
    </>
  );
};

export default SignupPage;
